import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EntityContextType } from 'nekst-api';
import { useChatWidgetContext } from '../../ChatWidgetContext';
import { EntityType, getEntityLabel } from './entityLinkUtils';
import styles from '../ChatWidget.module.scss';

interface EntityLinkMenuProps {
  type: EntityType;
  id: number;
  anchorRect: DOMRect;
  onClose: () => void;
}

export function EntityLinkMenu(props: EntityLinkMenuProps) {
  const { type, id, anchorRect, onClose } = props;
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { addContext } = useChatWidgetContext();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleAddToContext = () => {
    addContext({ type: type as EntityContextType, id });
    onClose();
  };

  const handleOpenEntity = () => {
    let path: string;

    switch (type) {
      case 'transaction':
        path = `/transactions/${id}`;
        break;
      case 'task':
        path = `${location.pathname}?taskId=${id}`;
        break;
      case 'contact':
        path = `/people/${id}`;
        break;
    }

    navigate(path);
    onClose();
  };

  const menuStyle: React.CSSProperties = {
    position: 'fixed',
    top: anchorRect.bottom + 4,
    left: anchorRect.left,
  };

  const entityLabel = getEntityLabel(type);

  return (
    <div
      ref={menuRef}
      className={styles.entityLinkMenu}
      style={menuStyle}
    >
      <button
        type="button"
        className={styles.entityLinkMenuItem}
        onClick={handleAddToContext}
      >
        Add to context
      </button>
      <button
        type="button"
        className={styles.entityLinkMenuItem}
        onClick={handleOpenEntity}
      >
        Open {entityLabel}
      </button>
    </div>
  );
}
